import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { validateFullName, validateEmail, validatePhone, toTitleCaseTurkish } from '../../../utils/turkishValidation';
import { formatTurkishPhone } from '../../../utils/phoneFormatter';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    experienceLevel: '',
    hasEquipment: '',
    location: '',
    agreeToTerms: false,
    agreeToPrivacy: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const isInAppBrowser = () => {
    const ua = navigator?.userAgent || '';
    return /Instagram|FBAV|FBAN|FB_IAB|FB4A|FBIOS|Facebook/i?.test(ua);
  };

  const getFriendlyAuthError = (error) => {
    const isInApp = isInAppBrowser();
    const inAppSuffix = isInApp
      ? ' Instagram/Facebook tarayıcısı girişleri engelliyor olabilir. Lütfen sağ üstteki menüden "Tarayıcıda Aç" (Safari/Chrome) seçeneğiyle devam edin.'
      : '';

    if (
      error?.name === 'AuthRetryableFetchError' ||
      error?.status === 503 ||
      error?.message?.includes('503') ||
      error?.message?.includes('fetch failed') ||
      error?.message?.includes('Failed to fetch')
    ) {
      return 'Giriş sunucularına şu an ulaşılamıyor. Lütfen sayfanızı yenileyip tekrar deneyin.' + inAppSuffix;
    }
    if (error?.message?.includes('User already registered') || error?.message?.includes('already registered')) {
      return 'Bu e-posta adresi zaten kayıtlı. Lütfen giriş yapın.';
    }
    if (error?.message?.includes('Network') || error?.message?.includes('network')) {
      return 'Bağlantı hatası. İnternet bağlantınızı kontrol edip tekrar deneyin.' + inAppSuffix;
    }
    return 'Kayıt başarısız oldu. Lütfen tekrar deneyin.' + inAppSuffix;
  };

  const experienceLevelOptions = [
    { value: 'beginner', label: 'Başlangıç - Yeni başlıyorum' },
    { value: 'intermediate', label: 'Orta - Deneyimliyim' },
    { value: 'advanced', label: 'İleri - Profesyonelim' }
  ];

  const equipmentOptions = [
    { value: 'yes', label: 'Evet, kendi ekipmanım var' },
    { value: 'no', label: 'Hayır, kiralama ihtiyacım var' },
    { value: 'planning', label: 'Almayı planlıyorum' }
  ];

  const locationOptions = [
    { value: 'istanbul', label: 'İstanbul' },
    { value: 'izmir', label: 'İzmir' },
    { value: 'antalya', label: 'Antalya' },
    { value: 'bodrum', label: 'Bodrum' },
    { value: 'marmaris', label: 'Marmaris' },
    { value: 'fethiye', label: 'Fethiye' },
    { value: 'cesme', label: 'Çeşme' },
    { value: 'alacati', label: 'Alaçatı' },
    { value: 'other', label: 'Diğer' }
  ];

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (password?.length >= 12) strength += 25;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength += 25;
    if (/\d/?.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/?.test(password)) strength += 10;
    return Math.min(strength, 100);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    let newValue = type === 'checkbox' ? checked : value;

    if (name === 'phone') {
      newValue = formatTurkishPhone(value);
    }

    if (name === 'fullName') {
      newValue = toTitleCaseTurkish(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateFullName(formData?.fullName);
    if (nameError) newErrors.fullName = nameError;

    const emailError = validateEmail(formData?.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validatePhone(formData?.phone);
    if (phoneError) newErrors.phone = phoneError;

    if (!formData?.password) {
      newErrors.password = 'Şifre gereklidir';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Şifre en az 8 karakter olmalıdır';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Şifre en az bir büyük harf, bir küçük harf ve bir rakam içermelidir';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Şifre tekrarı gereklidir';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    }

    if (!formData?.experienceLevel) {
      newErrors.experienceLevel = 'Deneyim seviyesi seçiniz';
    }

    if (!formData?.hasEquipment) {
      newErrors.hasEquipment = 'Ekipman durumu seçiniz';
    }

    if (!formData?.location) {
      newErrors.location = 'Konum seçiniz';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'Kullanım koşullarını kabul etmelisiniz';
    }

    if (!formData?.agreeToPrivacy) {
      newErrors.agreeToPrivacy = 'Gizlilik politikasını kabul etmelisiniz';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setServerError('');

    try {
      const { data, error } = await signUp(formData?.email, formData?.password, {
        full_name: formData?.fullName,
        phone: formData?.phone,
        experience_level: formData?.experienceLevel,
        has_equipment: formData?.hasEquipment,
        location: formData?.location
      });

      if (error) {
        console.error('Registration error:', error);
        setServerError(getFriendlyAuthError(error));
        setIsSubmitting(false);
        return;
      }

      if (data?.user) {
        navigate('/registration-success', {
          state: {
            email: formData?.email,
            fullName: formData?.fullName,
            experienceLevel: formData?.experienceLevel,
            hasEquipment: formData?.hasEquipment,
            location: formData?.location
          }
        });
      }
    } catch (err) {
      console.error('Registration error:', err);
      const isInApp = isInAppBrowser();
      const inAppSuffix = isInApp
        ? ' Instagram/Facebook tarayıcısı girişleri engelliyor olabilir. Lütfen sağ üstteki menüden "Tarayıcıda Aç" (Safari/Chrome) seçeneğiyle devam edin.'
        : '';

      if (
        err?.name === 'AuthRetryableFetchError' || err?.message?.includes('fetch failed') ||
        err?.message?.includes('Failed to fetch') ||
        err?.message?.includes('503')
      ) {
        setServerError('Giriş sunucularına şu an ulaşılamıyor. Lütfen sayfanızı yenileyip tekrar deneyin.' + inAppSuffix);
      } else {
        setServerError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' + inAppSuffix);
      }
      setIsSubmitting(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return 'var(--color-error)';
    if (passwordStrength < 70) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 40) return 'Zayıf';
    if (passwordStrength < 70) return 'Orta';
    return 'Güçlü';
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="space-y-4 md:space-y-5 lg:space-y-6">
        {serverError && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'var(--color-error-light, #fee)',
            border: '1px solid var(--color-error)',
            borderRadius: '8px',
            color: 'var(--color-error)',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icon name="AlertCircle" size={16} />
            <span>{serverError}</span>
          </div>
        )}

        <Input
          label="Ad Soyad"
          type="text"
          name="fullName"
          placeholder="Adınız ve soyadınız"
          value={formData?.fullName}
          onChange={handleInputChange}
          error={errors?.fullName}
          required
        />

        <Input
          label="E-posta Adresi"
          type="email"
          name="email"
          placeholder="ornek@email.com"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
        />

        <Input
          label="Telefon Numarası"
          type="tel"
          name="phone"
          placeholder="0532 123 45 67"
          value={formData?.phone}
          onChange={handleInputChange}
          error={errors?.phone}
          required
        />

        <div>
          <div style={{ position: 'relative' }}>
            <Input
              label="Şifre"
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="En az 8 karakter"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '38px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-muted-foreground)',
                padding: '4px'
              }}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>

          {formData?.password && (
            <div style={{ marginTop: '8px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '4px'
              }}>
                <span style={{
                  fontSize: '0.75rem',
                  color: 'var(--color-muted-foreground)'
                }}>
                  Şifre Gücü
                </span>
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  color: getPasswordStrengthColor()
                }}>
                  {getPasswordStrengthText()}
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'var(--color-muted)',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${passwordStrength}%`,
                  height: '100%',
                  backgroundColor: getPasswordStrengthColor(),
                  transition: 'all 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                }} />
              </div>
            </div>
          )}
        </div>

        <div style={{ position: 'relative' }}>
          <Input
            label="Şifre Tekrarı"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            placeholder="Şifrenizi tekrar giriniz"
            value={formData?.confirmPassword}
            onChange={handleInputChange}
            error={errors?.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            style={{
              position: 'absolute',
              right: '12px',
              top: '38px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-muted-foreground)',
              padding: '4px'
            }}
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        <Select
          label="Deneyim Seviyeniz"
          options={experienceLevelOptions}
          value={formData?.experienceLevel}
          onChange={(value) => handleSelectChange('experienceLevel', value)}
          placeholder="Seviye seçiniz"
          error={errors?.experienceLevel}
          required
        />

        <Select
          label="Ekipman Durumu"
          options={equipmentOptions}
          value={formData?.hasEquipment}
          onChange={(value) => handleSelectChange('hasEquipment', value)}
          placeholder="Ekipman durumunuzu seçiniz"
          error={errors?.hasEquipment}
          required
        />

        <Select
          label="Konum"
          options={locationOptions}
          value={formData?.location}
          onChange={(value) => handleSelectChange('location', value)}
          placeholder="Bulunduğunuz şehir"
          error={errors?.location}
          searchable
          required
        />

        <div className="space-y-3">
          <Checkbox
            label="Kullanım Koşullarını okudum ve kabul ediyorum"
            checked={formData?.agreeToTerms}
            onChange={(e) => handleInputChange({
              target: { name: 'agreeToTerms', type: 'checkbox', checked: e?.target?.checked }
            })}
            error={errors?.agreeToTerms}
            required
          />

          <Checkbox
            label="Gizlilik Politikasını okudum ve kabul ediyorum"
            checked={formData?.agreeToPrivacy}
            onChange={(e) => handleInputChange({
              target: { name: 'agreeToPrivacy', type: 'checkbox', checked: e?.target?.checked }
            })}
            error={errors?.agreeToPrivacy}
            required
          />
        </div>

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Hesap Oluşturuluyor...' : 'Hesap Oluştur'}
        </Button>
      </div>
    </form>
  );
};

export default RegistrationForm;